import React, { useMemo, useRef, useCallback } from 'react';
import styles from './ColorSelector.module.scss';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import colorPicker from '../../../../../../../../assets/image/color-picker.svg';

const ColorSelector = ({
  title = 'Icon Background',
  colors = [],
  includePicker = true,
  fieldName,
  value,
  setFieldValue,
  onSelect,
  onEyeClick,
  eyeColor = '#9E9E9E',
  hasView = true,
  viewToggled = true,
}) => {
  const norm = (c) => (typeof c === 'string' ? c.trim().toLowerCase() : c);
  const normalizedList = useMemo(() => colors.map(norm), [colors]);
  const selected = useMemo(() => norm(value), [value]);

  const commit = (hex) => {
    if (setFieldValue && fieldName) setFieldValue(fieldName, hex);
    onSelect?.(hex);
  };

  const isPickerSelected =
    includePicker && selected && !normalizedList.includes(selected);

  const colorInputRef = useRef(null);

  const openPicker = useCallback(() => {
    const el = colorInputRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      el.showPicker();
    } else {
      el.click();
    }
  }, []);

  return (
    <div className={styles.selector}>
      <div className={styles.header}>
        <p className={styles.title}>{title}</p>
        {hasView &&
          (viewToggled ? (
            <SvgWithBranding
              type='eyeBranding'
              color={eyeColor}
              className={styles.eyeIcon}
              onClick={onEyeClick}
            />
          ) : (
            <SvgWithBranding
              type='eyeBrandingStripped'
              color={eyeColor}
              className={styles.eyeIcon}
              onClick={onEyeClick}
            />
          ))}
      </div>

      <div className={styles.swatches}>
        {colors.map((c, i) => {
          const isCurrent = norm(c) === selected;
          return (
            <div
              key={i}
              className={`${styles.swatch} ${isCurrent ? styles.current : ''}`}
              role='button'
              tabIndex={0}
              aria-pressed={isCurrent}
              onClick={() => commit(c)}
              onKeyDown={(e) =>
                (e.key === 'Enter' || e.key === ' ') && commit(c)
              }
            >
              <div className={styles.color} style={{ backgroundColor: c }} />
            </div>
          );
        })}

        {includePicker && (
          <div
            className={`${styles.swatch} ${
              isPickerSelected ? styles.current : ''
            } ${styles.pickerSwatch}`}
            title='Custom color'
          >
            <img
              src={colorPicker}
              alt='color-picker'
              className={styles.image}
              role='button'
              tabIndex={0}
              onClick={openPicker}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') openPicker();
              }}
              aria-label='Open color picker'
            />
            <input
              ref={colorInputRef}
              type='color'
              className={styles.colorInput}
              value={
                selected && /^#[0-9a-f]{6}$/i.test(selected)
                  ? selected
                  : '#0957D0'
              }
              onChange={(e) => commit(e.target.value)}
              aria-label='Color value'
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ColorSelector;
